import React from 'react';
import { Shield, Lock, Scale, AlertCircle, FileText, Mail, Video, Users, Calendar, MapPin, Smartphone, CreditCard } from 'lucide-react';
import { Meta } from '../components/Meta';

export function TermsOfService() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <Meta
        title="Terms of Service | Mindful Family"
        description="Read our Terms of Service to understand your rights and responsibilities when using Mindful Family's wellness platform."
      />

      <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="flex items-center mb-8">
          <Scale className="h-8 w-8 text-accent-text mr-3" />
          <h1 className="text-3xl font-bold text-content">Terms of Service</h1>
        </div>

        <div className="prose max-w-none">
          <p className="text-content/80 mb-8">
            Last updated: January 2025
          </p>

          <div className="space-y-12">
            {/* Introduction */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-accent-text" />
                Introduction
              </h2>
              <p className="text-content/80">
                Welcome to Mindful Family. By accessing or using our platform at mindful.family (the "Service"),
                you agree to be bound by these Terms of Service. Please read these terms carefully before using our Service.
              </p>
            </section>

            {/* Definitions */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Definitions</h2>
              <ul className="list-disc pl-6 text-content/80 space-y-2">
                <li><strong>"Service"</strong> refers to the Mindful Family platform, including our website, mobile applications, and all related services</li>
                <li><strong>"Users"</strong> includes all individuals who access or use our Service</li>
                <li><strong>"Practitioners"</strong> refers to wellness professionals who offer services through our platform</li>
                <li><strong>"Content"</strong> includes text, images, videos, courses, recipes, and other materials posted on our Service</li>
                <li><strong>"Professional Members"</strong> refers to users with an active professional subscription</li>
                <li><strong>"Communities"</strong> refers to user-created groups within our platform</li>
                <li><strong>"Live Sessions"</strong> refers to real-time video streaming wellness sessions</li>
              </ul>
            </section>

            {/* Account Terms */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Account Terms</h2>
              <div className="space-y-4 text-content/80">
                <p>
                  You must be at least 18 years old to use this Service. Users between 13-17 years old may use the Service only with parental consent and supervision. You are responsible for maintaining
                  the security of your account and password.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <AlertCircle className="h-5 w-5 text-blue-500 mr-2" />
                    <span className="font-medium text-blue-800">Age Verification</span>
                  </div>
                  <p className="text-blue-800">
                    By creating an account, you represent and warrant that you meet the age requirements and have the legal capacity to enter into this agreement.
                  </p>
                </div>
                <h3 className="font-medium text-content">Professional Membership</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Professional membership requires a valid subscription</li>
                  <li>Professional members can create listings, host events, and offer services</li>
                  <li>Professional status may require verification of credentials</li>
                  <li>Subscription can be cancelled at any time</li>
                </ul>
              </div>
            </section>

            {/* Content Guidelines */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Content Guidelines</h2>
              <div className="space-y-4 text-content/80">
                <p>All content posted to the Service must:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Be accurate and not misleading</li>
                  <li>Respect intellectual property rights</li>
                  <li>Not contain harmful or malicious content</li>
                  <li>Not promote unauthorized medical claims</li>
                  <li>Comply with all applicable laws and regulations</li>
                  <li>Not contain hate speech, harassment, or discrimination</li>
                  <li>Not include spam, advertisements, or promotional content without authorization</li>
                </ul>
              </div>
            </section>

            {/* Community Guidelines */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <Users className="h-5 w-5 mr-2 text-accent-text" />
                Community Guidelines & Moderation
              </h2>
              <div className="space-y-4 text-content/80">
                <p>When participating in Communities on our platform, you agree to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Treat all members with respect and courtesy</li>
                  <li>Not engage in bullying, harassment, or intimidation</li>
                  <li>Not share personal information of others without consent</li>
                  <li>Follow the specific rules set by community administrators</li>
                  <li>Report any violations or concerning behavior to moderators</li>
                </ul>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                  <div className="flex items-center mb-2">
                    <AlertCircle className="h-5 w-5 text-amber-500 mr-2" />
                    <span className="font-medium text-amber-800">Moderation Rights</span>
                  </div>
                  <p className="text-amber-800">
                    We reserve the right to remove any content, suspend accounts, or ban users who violate these guidelines. Community administrators may also enforce additional rules within their communities.
                  </p>
                </div>
              </div>
            </section>

            {/* Live Streaming Terms */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <Video className="h-5 w-5 mr-2 text-accent-text" />
                Live Streaming Terms
              </h2>
              <div className="space-y-4 text-content/80">
                <p>When participating in live streaming sessions, you agree to the following:</p>
                <h3 className="font-medium text-content">Participant Conduct</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Behave respectfully toward hosts and other participants</li>
                  <li>Not disrupt sessions through inappropriate behavior or comments</li>
                  <li>Follow instructions provided by session hosts</li>
                  <li>Mute your microphone when not speaking (if applicable)</li>
                </ul>
                <h3 className="font-medium text-content mt-4">Recording & Privacy</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Sessions may be recorded for quality assurance or replay purposes</li>
                  <li>By participating, you consent to being recorded if recording is enabled</li>
                  <li>You may not record, screenshot, or redistribute session content without permission</li>
                  <li>Chat messages during live sessions may be visible to all participants</li>
                </ul>
                <h3 className="font-medium text-content mt-4">Intellectual Property</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>All live session content is owned by Mindful Family and/or the hosting practitioner</li>
                  <li>Unauthorized distribution of session content is prohibited</li>
                  <li>Premium membership grants access to view, not own, session content</li>
                </ul>
              </div>
            </section>

            {/* Course Terms */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Course Content Terms</h2>
              <div className="space-y-4 text-content/80">
                <p>When accessing courses on our platform:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Course access is granted for personal, non-commercial use only</li>
                  <li>You may not share your account credentials to give others access to courses</li>
                  <li>Course materials may not be downloaded, copied, or redistributed without permission</li>
                  <li>Quiz results and progress are stored for your personal tracking</li>
                  <li>Course creators retain intellectual property rights to their content</li>
                  <li>We reserve the right to update or remove course content at any time</li>
                </ul>
              </div>
            </section>

            {/* Booking Terms */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <Calendar className="h-5 w-5 mr-2 text-accent-text" />
                Booking & Cancellation Policy
              </h2>
              <div className="space-y-4 text-content/80">
                <p>When booking services with practitioners:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Bookings are agreements between you and the practitioner</li>
                  <li>Cancellation policies are set by individual practitioners</li>
                  <li>We recommend cancelling at least 24 hours before your appointment</li>
                  <li>Refunds for cancelled bookings are at the discretion of the practitioner</li>
                  <li>Repeated no-shows may result in booking restrictions</li>
                </ul>
                <p className="mt-4">
                  Mindful Family acts as an intermediary and is not responsible for disputes between users and practitioners regarding bookings.
                </p>
              </div>
            </section>

            {/* Practitioner Obligations */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Practitioner Obligations</h2>
              <div className="space-y-4 text-content/80">
                <p>Practitioners using our platform must:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Maintain valid certifications and qualifications</li>
                  <li>Provide accurate information about services and pricing</li>
                  <li>Maintain appropriate insurance coverage</li>
                  <li>Respond to client inquiries in a timely manner</li>
                  <li>Comply with professional standards and ethics</li>
                  <li>Honor bookings or provide adequate notice for cancellations</li>
                  <li>Not engage in any fraudulent or deceptive practices</li>
                </ul>
              </div>
            </section>

            {/* Job Board Terms */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Job Board Terms</h2>
              <div className="space-y-4 text-content/80">
                <p>When using our wellness job board:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Job listings must be for legitimate wellness-related positions</li>
                  <li>Employers must provide accurate information about positions and compensation</li>
                  <li>We do not guarantee employment outcomes</li>
                  <li>Users are responsible for verifying employer legitimacy</li>
                  <li>Discriminatory job postings are prohibited</li>
                </ul>
              </div>
            </section>

            {/* Payments and Fees */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <CreditCard className="h-5 w-5 mr-2 text-accent-text" />
                Payments and Fees
              </h2>
              <div className="space-y-4 text-content/80">
                <p>
                  Professional membership and certain features require payment. Payments are processed
                  securely through our payment providers.
                </p>
                <h3 className="font-medium text-content">Web Payments (Stripe)</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Web subscriptions are processed through Stripe</li>
                  <li>Professional membership is billed monthly at £5/month</li>
                  <li>Payments are non-refundable unless required by law</li>
                  <li>You may cancel your subscription at any time</li>
                </ul>
                <h3 className="font-medium text-content mt-4">Mobile App Payments (iOS/Android)</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>In-app purchases are processed through Apple App Store or Google Play</li>
                  <li>Subscription management and refunds are handled through the respective app store</li>
                  <li>Pricing may vary based on your region and app store policies</li>
                  <li>To cancel, manage your subscription through your device's app store settings</li>
                </ul>
                <h3 className="font-medium text-content mt-4">General Payment Terms</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Currency is displayed in GBP (British Pounds) unless otherwise specified</li>
                  <li>Users are responsible for applicable taxes</li>
                  <li>Failed payments may result in suspension of premium features</li>
                </ul>
              </div>
            </section>

            {/* Location Services */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <MapPin className="h-5 w-5 mr-2 text-accent-text" />
                Location-Based Services
              </h2>
              <div className="space-y-4 text-content/80">
                <p>Our "Near Me" and location-based features:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Require your consent to access device location</li>
                  <li>Use your location to show nearby practitioners, events, venues, and communities</li>
                  <li>Location data is used to calculate distances and is not shared with third parties</li>
                  <li>You can disable location services at any time through your device settings</li>
                  <li>Accuracy of location-based results depends on your device and settings</li>
                </ul>
              </div>
            </section>

            {/* Mobile App Terms */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <Smartphone className="h-5 w-5 mr-2 text-accent-text" />
                Mobile Application Terms
              </h2>
              <div className="space-y-4 text-content/80">
                <p>When using our mobile applications:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>The app requires an internet connection to function</li>
                  <li>Push notifications are used for important updates (can be disabled in settings)</li>
                  <li>App updates may be required to continue using the service</li>
                  <li>Some features may vary between web and mobile versions</li>
                  <li>App store terms and conditions also apply</li>
                </ul>
              </div>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Intellectual Property</h2>
              <div className="space-y-4 text-content/80">
                <p>
                  The Service and its original content (excluding content provided by users) remain the property
                  of Mindful Family. Users retain ownership of their content but grant us a license to use it
                  on the platform.
                </p>
                <h3 className="font-medium text-content">User Content License</h3>
                <p>
                  By posting content to our platform (including posts, recipes, courses, and comments), you grant Mindful Family a non-exclusive, worldwide, royalty-free license to use, display, and distribute your content within the Service.
                </p>
              </div>
            </section>

            {/* Termination */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Termination</h2>
              <div className="space-y-4 text-content/80">
                <p>
                  We reserve the right to terminate or suspend access to our Service immediately, without prior
                  notice, for conduct that we believe violates these Terms of Service or is harmful to other
                  users, our platform, or third parties, or for any other reason at our sole discretion.
                </p>
                <p>
                  Upon termination, your right to use the Service will cease immediately. If you wish to terminate your account, you may do so by contacting us or using the account deletion feature in your settings.
                </p>
              </div>
            </section>

            {/* Medical Disclaimer */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Medical and Wellness Disclaimer</h2>
              <div className="space-y-4 text-content/80">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                    <span className="font-medium text-red-800">Important Medical Notice</span>
                  </div>
                  <p className="text-red-800 mb-2">
                    <strong>Mindful Family is not a medical service provider.</strong> The information, content, and services provided through our platform are for informational and educational purposes only.
                  </p>
                  <ul className="list-disc pl-6 text-red-800 space-y-1">
                    <li>Our platform does not provide medical advice, diagnosis, or treatment</li>
                    <li>Wellness services offered by practitioners are not a substitute for professional medical care</li>
                    <li>Always consult with qualified healthcare professionals for medical concerns</li>
                    <li>Do not delay seeking professional medical advice because of information on our platform</li>
                    <li>In case of medical emergency, contact emergency services immediately</li>
                  </ul>
                </div>
                <p>
                  Practitioners on our platform are independent service providers. We do not endorse, recommend, or guarantee the quality of services provided by any practitioner. Users are responsible for evaluating practitioners and their services.
                </p>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Limitation of Liability</h2>
              <div className="space-y-4 text-content/80">
                <p>
                  Mindful Family shall not be liable for any indirect, incidental, special, consequential, or
                  punitive damages resulting from your use or inability to use the Service.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <AlertCircle className="h-5 w-5 text-amber-500 mr-2" />
                    <span className="font-medium text-amber-800">Important Notice</span>
                  </div>
                  <p className="text-amber-800">
                    We do not verify all claims or credentials of practitioners. Users are responsible for
                    verifying the qualifications of any practitioner they choose to work with.
                  </p>
                </div>
              </div>
            </section>

            {/* Dispute Resolution */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Dispute Resolution</h2>
              <div className="space-y-4 text-content/80">
                <p>
                  Any disputes arising from these Terms or your use of the Service will be resolved through binding arbitration rather than in court, except that you may assert claims in small claims court if your claims qualify.
                </p>
                <h3 className="font-medium text-content">Arbitration Process</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Disputes will be resolved through individual arbitration, not class action</li>
                  <li>Arbitration will be conducted by a neutral arbitrator</li>
                  <li>You may opt out of arbitration within 30 days of account creation</li>
                  <li>Arbitration will be conducted in English</li>
                </ul>
              </div>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Governing Law</h2>
              <div className="space-y-4 text-content/80">
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to conflict of law principles.
                </p>
                <p>
                  For users in the European Union, these Terms are subject to EU consumer protection laws and regulations.
                </p>
              </div>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4">Changes to Terms</h2>
              <div className="space-y-4 text-content/80">
                <p>
                  We reserve the right to modify or replace these terms at any time. We will provide notice
                  of any changes by posting the new Terms of Service on this page and updating the "Last updated"
                  date.
                </p>
                <p>
                  For material changes, we will provide additional notice through email or prominent notice on our Service at least 30 days before the changes take effect.
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-xl font-semibold text-content mb-4 flex items-center">
                <Mail className="h-5 w-5 mr-2 text-accent-text" />
                Contact Us
              </h2>
              <p className="text-content/80">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="mt-4 bg-accent-base/10 rounded-lg p-4">
                <p className="text-content/80">
                  Email: support@mindful.family<br />
                  Website: https://mindful.family
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
